// Agent-facing registry — aggregation point.
//
// Each family file exports one named `EffectEntry` per kind. Adding a
// new Strudel method means adding one named export to the matching
// family file. The exhaustiveness assertions below ensure every
// member of `SimpleEffectKind` and `ConditionalKind` has an entry.

import type { ConditionalKind, SimpleEffectKind } from '../types';
import type { EffectEntry, RegistryEntry } from './types';

import * as conditionals from './conditionals';
import * as delay from './delay';
import * as distortion from './distortion';
import * as dynamics from './dynamics';
import * as filter from './filter';
import * as modulation from './modulation';
import * as sample from './sample';
import * as space from './space';
import * as spatial from './spatial';
import * as structural from './structural';
import * as time from './time';
import * as tonal from './tonal';

// Spread each family's named exports together. The `import * as`
// namespaces are typed with their literal exported keys, so the
// resulting object's type is { gain: EffectEntry; amp: EffectEntry;
// lpf: EffectEntry; … } — the Record assignment below uses that.

const ALL_SIMPLE_EFFECTS = {
  ...delay,
  ...distortion,
  ...dynamics,
  ...filter,
  ...modulation,
  ...sample,
  ...space,
  ...spatial,
  ...structural,
  ...time,
  ...tonal,
};

const ALL_CONDITIONAL_EFFECTS = {
  ...conditionals,
};

// Load-bearing exhaustiveness checks. If you add a kind to
// `SimpleEffectKind` or `ConditionalKind` in `types.ts` without a
// matching named export in the right family file, one of these will
// fail with "Property 'foo' is missing in type ...".
const _SIMPLE_EXHAUSTIVE: Record<SimpleEffectKind, EffectEntry> =
  ALL_SIMPLE_EFFECTS;
const _CONDITIONAL_EXHAUSTIVE: Record<ConditionalKind, EffectEntry> =
  ALL_CONDITIONAL_EFFECTS;
void _SIMPLE_EXHAUSTIVE;
void _CONDITIONAL_EXHAUSTIVE;

const ALL: EffectEntry[] = [
  ...Object.values(ALL_SIMPLE_EFFECTS),
  ...Object.values(ALL_CONDITIONAL_EFFECTS),
];

export const REGISTRY: ReadonlyMap<string, RegistryEntry> = new Map(
  ALL.map((e) => [e.kind, e]),
);

export type { RegistryEntry } from './types';
export {
  type ArgSchema,
  type Category,
  type CodegenCtx,
  type EffectEntry,
  type ExpressionEntry,
  type Family,
  type MiniEntry,
  type ModulatorEntry,
  type ParseCtx,
  type StatementEntry,
} from './types';
